<template>
  <div>
    <p class="px-4 py-3 text-xs text-gray-600 border-b border-gray-200">
      {{ unlinkedCount }} of {{ pullRequests.length }} pull requests have no
      traceable customer request.
    </p>
    <table class="w-full text-[13px]" style="border-collapse: collapse">
      <!-- Percentage widths summing to 100, per app_ui/tables. -->
      <colgroup>
        <col style="width: 15%" />
        <col style="width: 32%" />
        <col style="width: 13%" />
        <col style="width: 14%" />
        <col style="width: 14%" />
        <col style="width: 12%" />
      </colgroup>
      <thead>
        <tr class="bg-gray-50 border-b border-gray-200 sticky top-0 z-[1]">
          <Hades_SortHeader
            v-for="col in columns"
            :key="col.key"
            :sort-key="col.key"
            :active-key="sortKey"
            :direction="sortDir"
            :th-class="thClassFor(col)"
            @sort="$emit('sort', $event)"
          >{{ col.label }}</Hades_SortHeader>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!pullRequests.length">
          <td colspan="6" class="px-4 py-6 text-gray-600">
            No pull requests in this category.
          </td>
        </tr>
        <tr
          v-for="pr in pullRequests"
          :key="pr.id"
          class="border-b border-gray-100 cursor-pointer"
          :class="pr.id === selectedId ? 'bg-gray-50' : 'hover:bg-gray-50/60'"
          @click="$emit('select', pr.id)"
        >
          <td class="pl-4 pr-2 py-2.5 align-top">
            <a
              :href="pr.url"
              target="_blank"
              rel="noopener"
              class="font-mono text-xs text-primary-700 hover:underline"
              @click.stop
            >{{ pr.repository }}#{{ pr.pr_number }}</a>
          </td>
          <td class="px-2 py-2.5 align-top text-gray-900">{{ pr.title }}</td>
          <td class="px-2 py-2.5 align-top text-gray-600">
            {{ label(DELIVERY_LABELS, pr.delivery_state) }}
          </td>
          <td
            class="px-2 py-2.5 align-top"
            :class="isLinked(pr) ? 'font-medium text-gray-800' : 'text-gray-400'"
          >
            {{ label(LINKAGE_LABELS, pr.customer_linkage) }}
          </td>
          <td class="px-2 py-2.5 align-top text-gray-600">
            {{ pr.engineer_id || '—' }}
          </td>
          <!-- UNMERGED IS A REAL STATE, not a blank. 22 of 357 PRs have no
               merged_at, and on a board about delivery that is the interesting
               subset, not a gap to hide behind an em-dash. -->
          <td class="pl-2 pr-4 py-2.5 align-top whitespace-nowrap">
            <span v-if="pr.merged_at" class="text-gray-600">
              {{ shortDate(pr.merged_at) }}
            </span>
            <span v-else class="text-gray-400 italic">Open</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import Hades_SortHeader from './Hades_SortHeader.vue';
import { DELIVERY_LABELS, LINKAGE_LABELS, label } from './hades_constants';

const TH = 'px-2 py-2.5 text-left text-[11px] font-semibold tracking-wide '
  + 'uppercase text-gray-600';

// `pr_number` sorts numerically within a repo; sorting on the rendered
// "repo#123" string would order 10 before 9. The parent's accessor handles the
// repo+number pairing.
const COLUMNS = [
  { key: 'pull_request', label: 'Pull request', first: true },
  { key: 'title', label: 'Title' },
  { key: 'delivery_state', label: 'Delivery' },
  { key: 'customer_linkage', label: 'Customer link' },
  { key: 'engineer_id', label: 'Engineer' },
  { key: 'merged_at', label: 'Merged', last: true },
];

export default {
  name: 'HadesPRTable',
  components: { Hades_SortHeader },
  props: {
    pullRequests: { type: Array, required: true },
    selectedId: { type: Number, default: null },
    // See the note in Hades_TicketTable — sort state is the parent's.
    sortKey: { type: String, default: null },
    sortDir: { type: String, default: 'asc' },
  },
  emits: ['select', 'sort'],
  data() {
    return {
      TH,
      TH_FIRST: `${TH} pl-4`,
      TH_LAST: `${TH} pr-4`,
      DELIVERY_LABELS,
      LINKAGE_LABELS,
      columns: COLUMNS,
    };
  },
  computed: {
    unlinkedCount() {
      return this.pullRequests.filter((p) => !(p.requirement_ids || []).length).length;
    },
  },
  methods: {
    label,
    isLinked(pr) {
      return !!(pr.requirement_ids || []).length;
    },
    thClassFor(col) {
      if (col.first) return this.TH_FIRST;
      if (col.last) return this.TH_LAST;
      return this.TH;
    },
    // UTC, matching how the timestamp is stored — in local time a PR merged at
    // 02:00 UTC displays as the previous day west of Greenwich, so the date
    // shown would not be the date in the database. Same rule as the
    // requirements list.
    shortDate(iso) {
      if (!iso) return '';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString(undefined,
        { month: 'short', day: 'numeric', timeZone: 'UTC' });
    },
  },
};
</script>
